import {useEffect, useState} from "react";

import {useAuth} from "../AuthContext";
import {clearCache, withCache} from "../cache";
import {useApi, usePlayMakersConfig} from "../PlayMakersProvider";
import {Quest, UserQuest} from "../types";


type fetchUserQuestMethod = (quiet?: boolean, refresh?: boolean) => Promise<UserQuest>;
interface UseUserQuestReturn {
  refresh: fetchUserQuestMethod;
  userQuest: UserQuest | null;
  fetched: boolean;
}

export const useUserQuest = (id?: string): UseUserQuestReturn => {
  const {getUserQuest} = useApi();

  const [userQuest, setUserQuest] = useState<UserQuest | null>(null);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetchUserQuest: fetchUserQuestMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getUserQuest", [id]);

    try {
      if(!id) throw new Error("No user quest to fetch");
      const fetchedUserQuest = await withCache(getUserQuest, [id], 120_000);
      setUserQuest(fetchedUserQuest);

      return fetchedUserQuest;
    } catch (err) {
      console.error("Error fetching user quest", err);
      throw new Error("Error fetching user quest", {cause: err});
    } finally {
      setFetched(true);
    }
  };

  useEffect(() => {
    if(id) fetchUserQuest();
  }, [id]);

  return {
    fetched,
    refresh: quiet => fetchUserQuest(quiet, true),
    userQuest
  };
};

type FetchUserQuestsMethod = (quiet?: boolean, refresh?: boolean) => Promise<UserQuest[]>;
type CompleteQuestMethod = (quest: Quest, creatorData?: Record<string, unknown>) => Promise<UserQuest>;
interface UseUserQuestsReturn {
  fetched: boolean;
  userQuests: UserQuest[];
  refresh: FetchUserQuestsMethod;

  // @deprecated Get steam profile via useAuth hook
  getSteamProfile: () => Promise<unknown>;
  // @deprecated Complete quests via useQuest hook
  completeQuest: CompleteQuestMethod;
  // @deprecated use userQuests instead
  userQuestList: UserQuest[];
}

export const useUserQuests = (params: {[key: string]: unknown} = {}): UseUserQuestsReturn => {
  const {PROJECT_ID} = usePlayMakersConfig();

  const {
    getUserQuests,
    getUserQuestsByQuest,
    completeQuest: completeQuestApi
  } = useApi();

  const {getSteamProfile} = useAuth();

  const [userQuests, setUserQuests] = useState<UserQuest[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetchUserQuests: FetchUserQuestsMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) {
      clearCache("getUserQuests");
      clearCache("getUserQuestsByQuest");
      clearCache("getUserQuestsByProject");
    }

    try {
      let quests;
      if(params.questId && typeof params.questId === "string")
        quests = await withCache(getUserQuestsByQuest, [params, params.questId], 120_000);
      else
        quests = await withCache(getUserQuests, [params, PROJECT_ID], 120_000);
      setUserQuests(quests);
      return quests;
    } catch (error) {
      console.error("Error fetching user quests", error);
      throw new Error("Error fetching user quests", {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const completeQuest: CompleteQuestMethod = async(quest, creatorData) => {
    try {
      console.warn("useUserQuests.completeQuest is deprecated, use useQuest hook instead");
      const questId = quest.id;
      const updatedQuest = await completeQuestApi(questId, creatorData);

      setUserQuests(prevQuestList =>
        prevQuestList.map(quest =>
          quest.id === questId ? {...quest, ...updatedQuest} : quest
        )
      );

      clearCache("getUserQuests");

      return updatedQuest;
    } catch (error) {
      console.error(`Error completing quest '${quest.id}'`, error);
      throw new Error(`Error completing quest '${quest.id}'`, {cause: error});
    }
  };

  useEffect(() => {
    fetchUserQuests();
  }, [JSON.stringify(params)]);

  return {
    completeQuest,
    fetched,
    getSteamProfile,
    refresh: quiet => fetchUserQuests(quiet, true),
    userQuestList: userQuests,
    userQuests
  };
};

export const useUserQuestsByQuest =
    (questId: string, params?: {[key: string]: any}): UseUserQuestsReturn => useUserQuests({...params, questId});
